import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from "react-router";
import {useTranslation} from 'react-i18next';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import Modal from "components/Modal";
import {
  callAlertWeb, callAppLoginWeb,
  callNativeWeb,
  footerShowEventWeb,
  hideProgressWeb,
  showProgressWeb
} from 'common/appInterface';
import "./DeliveryInput.css"
import { getVersionUrl } from 'common/cacheOverwrite';
import { isIOS } from "react-device-detect";

interface Juso {
  hoNm: string;
  floorNm: string;
}

type DetailStep = 'dong' | 'floor' | 'ho';

const DeliveryInputPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const screenId = "APPL14";
  const screenName = t("APPL14.title");

  const prevApply: any = location.state.apply ?? {};

  /** 캐시 무효화 — 캐시가 기록된 주소(homeRoadManagementNo)와 현재 주소가 다르면 이전 선택/조회 결과를 버린다 */
  const currentAddressKey: string = prevApply.homeRoadManagementNo ?? "";
  const cachedAddressKey: string = prevApply.cachedAddressKey ?? "";
  const addressChanged = !!cachedAddressKey && cachedAddressKey !== currentAddressKey;

  const [cardApply, setCardApply] = useState<Partial<CardApplyStep6>>({
    ...prevApply,
    thirdPartyReceiptYn: "Y",
    isManualInput: addressChanged ? false : (prevApply.isManualInput ?? false),
    dongNm: addressChanged ? "" : (prevApply.dongNm ?? ""),
    floorNm: addressChanged ? "" : (prevApply.floorNm ?? ""),
    hoNm: addressChanged ? "" : (prevApply.hoNm ?? ""),
    homeNewDetailAddress: addressChanged ? "" : prevApply.homeNewDetailAddress,
  } as any);

  /** 이전 페이지에서 캐시된 조회 결과 복원 (뒤로가기 대응). 주소 변경 시 버림 */
  const [dongList, setDongList] = useState<string[]>(addressChanged ? [] : (prevApply.dongList ?? []));
  const [floorHoList, setFloorHoList] = useState<Juso[]>(addressChanged ? [] : (prevApply.floorHoList ?? []));
  const [floorHoListDong, setFloorHoListDong] = useState<string>(addressChanged ? "" : (prevApply.floorHoListDong ?? ""));
  /** 해당 주소가 동 구조를 갖는지 여부 — 동 조회 응답이 [""] 이면 false */
  const [hasBuilding, setHasBuilding] = useState<boolean>(addressChanged ? true : (prevApply.hasBuilding ?? true));

  const [isSelectModal, setSelectModal] = useState(false);
  const [selectStep, setSelectStep] = useState<DetailStep>('dong');
  const [tempDong, setTempDong] = useState<string>("");
  const [tempFloor, setTempFloor] = useState<string>("");
  const [tempHo, setTempHo] = useState<string>("");

  const [isClick, setClick] = useState(false);

  const isManualInput = (cardApply as any).isManualInput === true;
  const dongNm = (cardApply as any).dongNm as string;
  const floorNm = (cardApply as any).floorNm as string;
  const hoNm = (cardApply as any).hoNm as string;
  const isSelectionComplete = !!((!hasBuilding || dongNm) && floorNm && hoNm);

  /** 현재 선택된 동에 해당하는 층 목록 */
  const floorOptions = useMemo(() => {
    const set = new Set<string>();
    floorHoList.forEach((j) => { if (j.floorNm) set.add(j.floorNm); });
    return Array.from(set);
  }, [floorHoList]);

  /** 현재 선택된 층에 해당하는 호 목록 */
  const hoOptions = useMemo(() => {
    return floorHoList.filter((j) => j.floorNm === tempFloor).map((j) => j.hoNm);
  }, [floorHoList, tempFloor]);

  /** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) — 선택 모달이 열려 있으면 먼저 닫는다 */
  const callbackBackkeyEvent = useCallback(() => {
    if (isSelectModal) { setSelectModal(false); return; }
    navigate(-1);
  }, [navigate, isSelectModal]);

  /** 모달 닫기 */
  const closeSelectModal = useCallback(() => {
    setSelectModal(false);
  }, []);

  /** 층/호 응답 검증 — 데이터 0개이거나 floorNm/hoNm에 빈 문자열이 하나라도 있으면 수동 입력 폴백 */
  const needsManualFallback = (list: Juso[]): boolean =>
    list.length === 0 || list.some((j) => !j || !j.floorNm || !j.hoNm);

  /** 선택 모드에서 상세 주소 문자열 합성 — 빈 값은 제외해 중복 공백/선행 공백 방지 */
  const composeDetailAddress = (d: string, f: string, h: string): string =>
    [d, f, h].filter(Boolean).join(" ");

  /** 수동 입력 모드로 전환 — 선택된 동/층/호 및 상세주소 모두 초기화 */
  const switchToManualMode = useCallback((initialDetail: string = "") => {
    setCardApply((prev: any) => ({
      ...prev,
      isManualInput: true,
      dongNm: "",
      floorNm: "",
      hoNm: "",
      homeNewDetailAddress: initialDetail,
    }));
    setTempDong("");
    setTempFloor("");
    setTempHo("");
    setSelectModal(false);
  }, []);

  /** 동 목록 조회 */
  const fetchDongList = useCallback(async (): Promise<string[]> => {
    const params = {
      ...location.state.query,
      admCd: cardApply.admCd,
      rnMgtSn: cardApply.homeRoadManagementNo,
      udrtYn: cardApply.udrtYn,
      buldMnnm: cardApply.buldMnnm,
      buldSlno: cardApply.buldSlno,
    };
    const response = await client.get("/api/app/apply/juso/searchDetail/dong", { params });
    if (response.data?.code === "02") {
      // 세션 만료 처리
      // 로그인 페이지 이동
      callAppLoginWeb();
      return [];
    }

    const list: string[] = (response.data?.response ?? response.data ?? []) as string[];
    return Array.isArray(list) ? list : [];
  }, [location.state.query, cardApply.homeRoadManagementNo, cardApply.admCd, cardApply.buldMnnm, cardApply.buldSlno, cardApply.udrtYn]);

  /** 층/호 목록 조회 */
  const fetchFloorHoList = useCallback(async (dong: string): Promise<Juso[]> => {
    const params = {
      ...location.state.query,
      admCd: cardApply.admCd,
      rnMgtSn: cardApply.homeRoadManagementNo,
      udrtYn: cardApply.udrtYn,
      buldMnnm: cardApply.buldMnnm,
      buldSlno: cardApply.buldSlno,
      dongNm: dong
    };
    const response = await client.get("/api/app/apply/juso/searchDetail/floorho", { params });
    if (response.data?.code === "02") {
      // 세션 만료 처리
      // 로그인 페이지 이동
      callAppLoginWeb();
      return [];
    }

    const results = response.data?.response?.results ?? [];
    const list: Juso[] = results as Juso[];
    return Array.isArray(list) ? list : [];
  }, [location.state.query, cardApply.homeRoadManagementNo, cardApply.admCd, cardApply.buldMnnm, cardApply.buldSlno, cardApply.udrtYn]);

  /** 바텀모달 열기 */
  const openSelectModal = useCallback(async (step: DetailStep = 'dong') => {
    setTempDong(dongNm || "");
    setTempFloor(floorNm || "");
    setTempHo(hoNm || "");

    if (step === 'dong') {
      // 동 리스트는 마운트 시 resolveDongMode에서 이미 조회·분기 완료된 상태.
      // Building 드롭다운은 케이스 3(정상)에서만 노출되므로 여기서는 모달만 연다.
      setSelectStep('dong');
      setSelectModal(true);
      return;
    }

    if (step === 'floor' || step === 'ho') {
      if (floorHoList.length === 0 || floorHoListDong !== dongNm) {
        if (hasBuilding && !dongNm) return;
        showProgressWeb();
        try {
          const list = await fetchFloorHoList(dongNm);
          if (needsManualFallback(list)) {
            switchToManualMode();
            hideProgressWeb();
            return;
          }
          setFloorHoList(list);
          setFloorHoListDong(dongNm);
        } catch (e: any) {
          callAlertWeb("ERROR", "99", e?.message ?? "");
          switchToManualMode();
          hideProgressWeb();
          return;
        }
        hideProgressWeb();
      }
      setSelectStep(step);
      setSelectModal(true);
    }
  }, [dongNm, floorNm, hoNm, floorHoList, floorHoListDong, fetchFloorHoList, hasBuilding, switchToManualMode]);

  /** 동 조회 및 모드 판단 — 페이지 마운트 시 한 번 실행 */
  const resolveDongMode = useCallback(async () => {
    showProgressWeb();

    let list: string[] = dongList;

    if (!list || list.length === 0) {
      try {
        list = await fetchDongList();
      } catch (e: any) {
        callAlertWeb("ERROR", "99", e?.message ?? "");
        switchToManualMode();
        hideProgressWeb();
        return;
      }
      setDongList(list);
    }

    // 케이스 1: 0개 → 직접 입력
    if (list.length === 0) {
      switchToManualMode();
      hideProgressWeb();
      return;
    }

    // 케이스 4: [""] → 건물 없음 모드 (층/호 미리 조회)
    if (list.length === 1) {
      if (list[0] === "") {
        setHasBuilding(false);
        try {
          const floors = await fetchFloorHoList("");
          if (needsManualFallback(floors)) {
            switchToManualMode();
            hideProgressWeb();
            return;
          }
          setFloorHoList(floors);
          setFloorHoListDong("");
          setCardApply((prev: any) => ({ ...prev, isManualInput: false, dongNm: "" }));
        } catch (e: any) {
          callAlertWeb("ERROR", "99", e?.message ?? "");
          switchToManualMode();
          hideProgressWeb();
          return;
        }
      } else {
        switchToManualMode();
        hideProgressWeb();
        return;
      }

      hideProgressWeb();

      setTempDong('');
      setTempFloor(floorNm || '');
      setTempHo(hoNm || '');
      setSelectStep('floor');
      setSelectModal(true);
      return;
    }

    // 케이스 2 또는 혼합: 1개이지만 ""가 아니거나, 2개 이상인데 ""가 섞임 → 직접 입력
    if (list.length === 1 || list.includes("")) {
      switchToManualMode();
      hideProgressWeb();
      return;
    }

    // 케이스 3: 정상 — 2개 이상 & "" 없음
    setHasBuilding(true);
    openSelectModal('dong');
    hideProgressWeb();
  }, [fetchDongList, fetchFloorHoList, switchToManualMode, dongList, floorNm, hoNm, openSelectModal]);

  /** 바텀모달 - 다음 버튼 */
  const clickBottomNext = useCallback(async () => {
    if (selectStep === 'dong') {
      if (!tempDong) return;
      // 동이 바뀌었거나 캐시가 비었다면 층/호 재조회
      const dongChanged = tempDong !== dongNm;

      if (floorHoList.length === 0 || floorHoListDong !== tempDong) {
        showProgressWeb();
        try {
          const list = await fetchFloorHoList(tempDong);
          if (needsManualFallback(list)) {
            switchToManualMode(tempDong);
            hideProgressWeb();
            return;
          }
          setFloorHoList(list);
          setFloorHoListDong(tempDong);
        } catch (e: any) {
          callAlertWeb("ERROR", "99", e?.message ?? "");
          switchToManualMode();
          hideProgressWeb();
          return;
        }
        hideProgressWeb();
      }
      if (dongChanged) {
        setTempFloor("")
        setTempHo("")
      }

      setCardApply((prev: any) => ({
        ...prev,
          isManualInput: false,
          dongNm: tempDong,
          ...(dongChanged ? { floorNm: "", hoNm: "" } : {}),
      }));

      setSelectStep('floor');
      return;
    }

    if (selectStep === 'floor') {
      if (!tempFloor) return;

      const floorChanged = tempFloor !== floorNm;
      if (floorChanged) {
        setTempHo('');
      }

      setCardApply((prev: any) => ({
        ...prev,
        floorNm: tempFloor,
        ...(floorChanged ? { hoNm: "" } : {}),
      }));

      setSelectStep('ho');
      return;
    }

    if (selectStep === 'ho') {
      if (!tempHo) return;

      setCardApply((prev: any) => ({
        ...prev,
        hoNm: tempHo,
        homeNewDetailAddress: composeDetailAddress(tempDong, tempFloor, tempHo),
      }));
      setSelectModal(false);
    }
  }, [selectStep, tempDong, tempFloor, tempHo, floorHoList.length, floorHoListDong, fetchFloorHoList, dongNm, floorNm, switchToManualMode]);

  /** 바텀모달 - 직접 입력하기 */
  const clickManualInput = useCallback(() => {
    setCardApply({
      ...cardApply,
      isManualInput: true,
      dongNm: "",
      floorNm: "",
      hoNm: "",
      homeNewDetailAddress: "",
    } as any);
    setTempDong("");
    setTempFloor("");
    setTempHo("");
    setSelectModal(false);
  }, [cardApply]);

  /** 다음 페이지로 이동 시 선택 정보 및 조회 캐시를 함께 실어 보냄 */
  const buildApplyForNav = useCallback((): any => ({
    ...cardApply,
    dongNm,
    floorNm,
    hoNm,
    isManualInput,
    dongList,
    floorHoList,
    floorHoListDong,
    hasBuilding,
    cachedAddressKey: cardApply.homeRoadManagementNo ?? "",
  }), [cardApply, dongNm, floorNm, hoNm, isManualInput, dongList, floorHoList, floorHoListDong, hasBuilding]);

  /** Next 처리 — 주소검증은 확인 페이지의 Confirm 시점에서 수행 */
  const clickNext = useCallback(() => {
    if (isClick) {
      console.log("Already Clicked...");
      return;
    }

    setClick(true);

    const detailAddress = isManualInput
      ? (cardApply.homeNewDetailAddress ?? "")
      : composeDetailAddress(dongNm, floorNm, hoNm);

    const applyForNav = { ...buildApplyForNav(), homeNewDetailAddress: detailAddress };

    const updatedState = {
      ...location.state,
      apply: applyForNav
    };

    navigate(location.pathname, {
      state: updatedState,
      replace: true,
    })
    navigate("/apply/step6/input/confirm", {state: updatedState});

    setClick(false);
  }, [isClick, location.state, location.pathname, cardApply.homeNewDetailAddress, isManualInput, dongNm, floorNm, hoNm, buildApplyForNav, navigate]);

  /** 주소변경 처리 */
  const clickChangeAddress = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);
    console.log(cardApply);

    if (!isManualInput && !isSelectionComplete) {
      resolveDongMode();
    }

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  /** 메인 Next 활성화 여부 */
  const canProceed = isManualInput
    ? !!(cardApply.homeNewDetailAddress && cardApply.homeNewDetailAddress.length > 0)
    : isSelectionComplete;

  /** 바텀모달 내용 */
  const renderSelectModalBody = () => {
    if (selectStep === 'dong') {
      return (
        <>
          <p className="bottomSheetTitle">{t(screenId + ".modal01", "Please select the building")}</p>
          <ul className="bottomSheetList">
            {dongList.map((d, i) => (
              <li key={`${d}-${i}`} className={tempDong === d ? "selected" : ""} onClick={() => setTempDong(d)}>
                <span>{d}</span>
                {tempDong === d && <i className="checkIcon" />}
              </li>
            ))}
          </ul>
        </>
      );
    }
    if (selectStep === 'floor') {
      return (
        <>
          <p className="bottomSheetTitle">{t(screenId + ".modal02", "Please select the floor")}</p>
          <ul className="bottomSheetList">
            {floorOptions.map((f, i) => (
              <li key={`${f}-${i}`} className={tempFloor === f ? "selected" : ""} onClick={() => { setTempFloor(f); setTempHo(""); }}>
                <span>{f}</span>
                {tempFloor === f && <i className="checkIcon" />}
              </li>
            ))}
          </ul>
        </>
      );
    }
    return (
      <>
        <p className="bottomSheetTitle">{t(screenId + ".modal03", "Please select the room number")}</p>
        <ul className="bottomSheetList">
          {hoOptions.map((h, i) => (
            <li key={`${h}-${i}`} className={tempHo === h ? "selected" : ""} onClick={() => setTempHo(h)}>
              <span>{h}</span>
              {tempHo === h && <i className="checkIcon" />}
            </li>
          ))}
        </ul>
      </>
    );
  };

  const bottomNextDisabled =
    (selectStep === 'dong' && !tempDong) ||
    (selectStep === 'floor' && !tempFloor) ||
    (selectStep === 'ho' && !tempHo);

  return (
    <div id="wrap" className={isIOS ? "isIOS" : ""}>
      <div id="container" className="pcHidden">
        <section className="contents pb64-mb">
          <div className="singUpWrapSub">
            <div className="inputBoxTy01">
              <p className="label">{t("APPL14.header01")}</p>
              <p className="addressNum">{cardApply.homeZipCode}</p>
              <p className="address">{cardApply.homeEngAddress}</p>
              <button type="button" className="add_btn" onClick={clickChangeAddress}>{t("APPL14.button01")}</button>
            </div>

            {isManualInput ? (
              <div className="inputBoxTy01 mgt50">
                <label>
                  <input placeholder=" " type="text" value={cardApply.homeNewDetailAddress ?? ""} onChange={(e) => setCardApply({ ...cardApply, homeNewDetailAddress: e.target.value })} />
                  <span>{t("APPL14.header02")}</span>
                  <p className="message">{t("APPL14.explan01")}</p>
                </label>
              </div>
            ) : (
              <>
                {hasBuilding && (
                  <div className="inputBoxTy01 selectBox mgt50" onClick={() => {openSelectModal('dong')}}>
                    <p className="label">{t(screenId + ".building", "Building")}</p>
                    {dongNm
                      ? <p className="selectedValue">{dongNm}</p>
                      : <p className="selectedValue placeholder">{t(screenId + ".modal01", "Please select the building")}</p>}
                    <img src={getVersionUrl("/assets/images/ico/btn-dropdown-down@3x.png")} alt={""} className="arrowDown" />
                    <hr />
                  </div>
                )}

                {(!hasBuilding || dongNm) && (
                  <div className="inputBoxTy01 selectBox mgt50" onClick={() => {openSelectModal('floor')}}>
                    <p className="label">{t(screenId + ".floor", "Floor")}</p>
                    {floorNm
                      ? <p className="selectedValue">{floorNm}</p>
                      : <p className="selectedValue placeholder">{t(screenId + ".modal02", "Please select the floor")}</p>}
                    <img src={getVersionUrl("/assets/images/ico/btn-dropdown-down@3x.png")} alt={""} className="arrowDown" />
                    <hr />
                  </div>
                )}
                {(!hasBuilding || dongNm) && floorNm && (
                  <div className="inputBoxTy01 selectBox mgt50" onClick={() => {openSelectModal('ho')}}>
                    <p className="label">{t(screenId + ".roomno", "Room Number")}</p>
                    {hoNm
                      ? <p className="selectedValue">{hoNm}</p>
                      : <p className="selectedValue placeholder">{t(screenId + ".modal03", "Please select the room number")}</p>}
                    <img src={getVersionUrl("/assets/images/ico/btn-dropdown-down@3x.png")} alt={""} className="arrowDown" />
                    <hr />
                  </div>
                )}
              </>
            )}
          </div>
        </section>
        <div className="btnArea">
          <div className="btnWrapTy01">
            <button type="button" className="btnPrimaryTy01" disabled={isClick || !canProceed} onClick={clickNext}><span>{t("COMMON.button.next")}</span></button>
          </div>
        </div>
      </div>

      {/* 동/층/호 선택 바텀시트 */}
      <Modal isOpen={isSelectModal} contentLabel="selectModal" onRequestClose={closeSelectModal} className="popTyBottomSheet" overlayClassName="dim" closeTimeoutMS={300}>
        {renderSelectModalBody()}
        <button type="button" className="manualInputBtn" onClick={clickManualInput}>{t(screenId + ".modal04", "Enter Manually")}</button>
        <div className="btnWrapTy01">
          <button type="button" className="btnPrimaryTy01" disabled={bottomNextDisabled} onClick={clickBottomNext}><span>{t("COMMON.button.next")}</span></button>
        </div>
      </Modal>

    </div>
  );
};

export default DeliveryInputPage;
